import { useState, FormEvent, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import {
  signUpWithEmail,
  signInWithEmail,
  getPartnerRefCode,
  writePendingCheckoutLocal,
  PendingCheckout,
} from '../lib/auth';
import { validatePassword } from '../lib/passwordSecurity';
import { TIER_CATALOG } from '../lib/billing';
import { isNativeApp, onReturnFromEmailConfirm, onAppResume } from '../lib/nativeShell';

/**
 * Reads ?plan=&billing= from the current URL and validates them into a
 * PendingCheckout, or returns null. Marketing pricing.html sends visitors
 * to /signup?plan=tierN&billing=(monthly|annual) so we can stash the
 * chosen plan before signup and route them to /checkout after email
 * verification — see task #142.
 */
function readPlanFromUrl(): PendingCheckout | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const tier = params.get('plan');
    const billing = params.get('billing');
    if (tier !== 'tier1' && tier !== 'tier2' && tier !== 'tier3') return null;
    if (billing !== 'monthly' && billing !== 'annual') return null;
    return { tier, billing };
  } catch { return null; }
}

/** Human-readable summary of the chosen plan for the banner. */
function planLabel(pc: PendingCheckout): string {
  const cat = TIER_CATALOG[pc.tier];
  if (pc.billing === 'annual') {
    return `${cat.label} Annual — £${cat.yearlyGbp}/year (save £${cat.monthlyGbp * 12 - cat.yearlyGbp})`;
  }
  return `${cat.label} Monthly — £${cat.monthlyGbp}/month`;
}

export function Signup() {
  const nav = useNavigate();
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [partnerRef, setPartnerRef] = useState('');
  const [showPartnerRef, setShowPartnerRef] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate state for the "email already in use" case so we can render a
  // helpful CTA to the login page instead of a flat error banner.
  const [emailExists, setEmailExists] = useState(false);
  const [done, setDone] = useState(false);
  const native = isNativeApp();
  const [continuing, setContinuing] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const continuingRef = useRef(false);

  // App only: once they've tapped the link in their email, sign them in
  // with the details they just typed (still in memory) — no retyping.
  // `silent` = triggered automatically (app resumed / deep link), so we
  // don't nag if they simply haven't tapped the email link yet.
  async function continueAfterConfirm(silent: boolean) {
    if (continuingRef.current) return;
    continuingRef.current = true;
    setContinuing(true);
    if (!silent) setConfirmMsg(null);
    try {
      await signInWithEmail(email, password);
      // Session is now set → the /signup route redirects to "/", which
      // sends a new company to the plans screen.
      nav('/', { replace: true });
    } catch (err) {
      if (!silent) {
        const msg = err instanceof Error ? err.message : '';
        setConfirmMsg(
          /confirm/i.test(msg)
            ? "We can't see the confirmation yet. Tap the link in the email first, then try again."
            : msg || 'Something went wrong. Please try again.',
        );
      }
    } finally {
      continuingRef.current = false;
      setContinuing(false);
    }
  }

  useEffect(() => {
    if (!done || !native) return;
    const offLink = onReturnFromEmailConfirm(() => void continueAfterConfirm(true));
    const offResume = onAppResume(() => void continueAfterConfirm(true));
    return () => { offLink(); offResume(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, native]);

  // Parse the marketing pricing-page plan once per mount. useMemo is fine
  // here — window.location.search is stable for the lifetime of this
  // component (React Router doesn't re-mount on query changes on this
  // route, and this page has no in-page navigation).
  const pendingCheckout = useMemo(readPlanFromUrl, []);

  // Preserve ?plan=&billing= on the "Sign in instead" bounce so a
  // pre-existing customer who accidentally hit signup still ends up in
  // /checkout after login, not at /paywall picking the same plan again.
  const loginBounceQuery = useMemo(() => {
    const parts: string[] = [];
    if (email) parts.push(`email=${encodeURIComponent(email)}`);
    if (pendingCheckout) {
      parts.push(`plan=${pendingCheckout.tier}`);
      parts.push(`billing=${pendingCheckout.billing}`);
    }
    return parts.length > 0 ? `?${parts.join('&')}` : '';
  }, [email, pendingCheckout]);

  // Stash the pending checkout in localStorage on mount so we've got it
  // saved even if the user closes the tab without submitting (they may
  // come back later via the verification email on the same device).
  // localStorage is per-device, user_metadata will pick up on signup.
  useEffect(() => {
    if (pendingCheckout) writePendingCheckoutLocal(pendingCheckout);
  }, [pendingCheckout]);

  // Prefill promo code from URL ?ref= or persisted localStorage.
  // If we found one, auto-open the field so the user sees what's being applied.
  useEffect(() => {
    const code = getPartnerRefCode();
    if (code) {
      setPartnerRef(code);
      setShowPartnerRef(true);
    }
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailExists(false);
    setSubmitting(true);
    try {
      // Validate password: 12-char minimum + Have I Been Pwned breach check.
      // The breach check uses k-anonymity (only the first 5 chars of the
      // SHA-1 hash leave the device). See lib/passwordSecurity.ts.
      const pwdError = await validatePassword(password);
      if (pwdError) {
        setError(pwdError);
        setSubmitting(false);
        return;
      }
      await signUpWithEmail({
        email,
        password,
        fullName,
        orgName,
        partnerRef,
        // Persist the plan the visitor picked on the marketing site
        // straight into user_metadata so it survives cross-device email
        // verification (localStorage only covers same-device).
        pendingCheckout: pendingCheckout ?? undefined,
      });
      setDone(true);
    } catch (err) {
      // Special case: email already has an account — show a friendly
      // CTA to the login flow rather than a flat error message.
      const code = (err as { code?: string } | null)?.code;
      if (code === 'email_exists') {
        setEmailExists(true);
      } else {
        setError(err instanceof Error ? err.message : 'Signup failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done && native) {
    return (
      <AuthLayout title="Check your email">
        <h1>Verify your email</h1>
        <p className="muted">
          We've sent a link to <strong>{email}</strong>. Tap it to confirm
          your account, then come back here — we'll carry on automatically.
        </p>
        {confirmMsg && <div className="banner" style={{ marginTop: 12 }}>{confirmMsg}</div>}
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: 16 }}
          disabled={continuing}
          onClick={() => void continueAfterConfirm(false)}
        >
          {continuing ? 'Checking…' : "I've confirmed my email — continue"}
        </button>
        <p className="auth-footer">
          <Link to={`/login${loginBounceQuery}`}>Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Check your email">
        <h1>Verify your email</h1>
        <p className="muted">
          We've sent a verification link to <strong>{email}</strong>.
          Tap it to confirm your account
          {pendingCheckout
            ? ' — then we\'ll take you to secure checkout at Stripe.'
            : ', then come back and sign in.'}
        </p>
        <p className="auth-footer">
          <Link to={`/login${loginBounceQuery}`}>Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  // ------------------------------------------------------------------
  // iOS / Android native shells: signup is ENABLED again as of v1.1.
  //
  // History: For v1.0 (approved 9 Sep 2026) we removed signup from
  // native entirely to sidestep Apple Guideline 3.1.1's In-App Purchase
  // requirement — the app was sign-in-only, plans were purchased on
  // the web. That worked for review but produced a real customer failure
  // (fresh download, no way to create an account). For v1.1 we've added
  // In-App Purchase via RevenueCat, so a native user can:
  //   1. Sign up here (creates Supabase account, no plan yet)
  //   2. RequireSubscription bounces to /paywall
  //   3. /paywall shows Apple IAP tiers (see Paywall.tsx native branch)
  //   4. Apple charges the user, RC webhook flips subscription_status
  //   5. RequireSubscription lets them in
  //
  // The invite flow still works — /invite/:token skips this page entirely
  // — so crew members joining an existing paid org are unaffected.
  // ------------------------------------------------------------------

  return (
    <AuthLayout title="Sign up">
      <h1>Create your account</h1>
      <p className="muted">
        Two minutes to set up. You'll run this account for your business.
      </p>

      {pendingCheckout && (
        // Chosen-plan banner — tells the visitor which pricing tier they
        // clicked on the marketing site, and that they'll be sent to
        // checkout right after they verify their email. Reassures them
        // the plan hasn't been lost through the signup step.
        <div
          className="banner"
          style={{
            padding: '10px 12px',
            marginBottom: 12,
            fontSize: 13,
            background: '#E8F1EC',
            border: '1px solid var(--success, #16A34A)',
            borderRadius: 6,
            color: 'var(--ink)',
          }}
        >
          <strong>You're subscribing to {planLabel(pendingCheckout)}.</strong><br />
          First create your account — we'll take you to secure checkout right after.
        </div>
      )}

      {error && <div className="banner banner-error">{error}</div>}

      {emailExists && (
        <div className="banner banner-error" style={{ display: 'block' }}>
          <strong>{email}</strong> already has a Safe and Sorted account.{' '}
          <Link to={`/login${loginBounceQuery}`} style={{ textDecoration: 'underline', fontWeight: 600 }}>
            Sign in instead →
          </Link>
          <p style={{ margin: '6px 0 0', fontSize: '0.9em', opacity: 0.85 }}>
            Forgotten your password? Use the "Forgot password" link on the sign-in page.
          </p>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="full-name">Your name</label>
          <input
            id="full-name"
            type="text"
            autoComplete="name"
            enterKeyHint="next"
            autoFocus
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="org-name">Company / organisation name</label>
          <input
            id="org-name"
            type="text"
            autoComplete="organization"
            enterKeyHint="next"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            enterKeyHint="next"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            enterKeyHint="go"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <p className="muted" style={{ marginTop: 6, fontSize: '0.85em' }}>
            At least 12 characters. A memorable passphrase like four random
            words is stronger than a short complex one.
          </p>
        </div>

        {/* Promo/partner code — hidden on iOS/Android native shells to comply
            with Apple Guideline 3.1.1 (no in-app mechanisms that unlock plans
            outside of In-App Purchase). Codes still work on the web PWA. */}
        {!isNativeApp() && !showPartnerRef && (
          <p className="auth-footer" style={{ margin: '4px 0 16px' }}>
            <button
              type="button"
              onClick={() => setShowPartnerRef(true)}
              style={{ background: 'none', border: 0, color: 'var(--ink-soft)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
            >
              Got a promo code?
            </button>
          </p>
        )}

        {!isNativeApp() && showPartnerRef && (
          <div className="field">
            <label htmlFor="partner-ref">Promo / partner code <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
            <input
              id="partner-ref"
              type="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. ACME-50"
              value={partnerRef}
              onChange={(e) => setPartnerRef(e.target.value.toUpperCase())}
            />
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="auth-footer">
        Already have an account? <Link to={`/login${loginBounceQuery}`}>Sign in</Link>
      </p>
    </AuthLayout>
  );
}
